import type { MapEvent } from "ol";
import type OlMap from "ol/Map";
import type { ViewStateLayerStateExtent } from "ol/View";
import { equals } from "ol/array";
import { toPromise } from "ol/functions";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { MapControl, useNgwMapContext } from "@nextgis/react-ngw-map";
import type { ControlOptions } from "@nextgis/webmap";

import { useTranslation } from "../../i18n";

import "./AttributionControl.css";

interface AttributionControlProps extends ControlOptions {
  attributions?: string | string[];
  className?: string;
  expandClassName?: string;
  label?: ReactNode;
  smallBreakpointPx?: number;
  tipLabel?: string;
}

function classNames(
  ...items: Array<string | false | null | undefined | Record<string, boolean>>
) {
  return items
    .flatMap((item) => {
      if (!item) return [];
      if (typeof item === "string") return [item];
      return Object.entries(item)
        .filter(([, enabled]) => enabled)
        .map(([name]) => name);
    })
    .join(" ");
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10v6" />
      <path d="M12 7h.01" />
    </svg>
  );
}

function AttributionList({ items }: { items: string[] }) {
  return (
    <ul>
      {items.map((html, index) => (
        <li key={index} dangerouslySetInnerHTML={{ __html: html }} />
      ))}
    </ul>
  );
}

function AttributionModal({
  closeLabel,
  items,
  onClose,
  title,
}: {
  closeLabel: string;
  items: string[];
  onClose: () => void;
  title: string;
}) {
  return (
    <div
      className="ol-attribution-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        className="ol-attribution-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="ol-attribution-modal-header">
          <strong>{title}</strong>
          <button type="button" aria-label={closeLabel} onClick={onClose}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <AttributionList items={items} />
      </div>
    </div>
  );
}

function normalizeAttributions(value: unknown): unknown[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export function AttributionControl({
  attributions,
  className = "ol-attribution",
  expandClassName,
  label = <InfoIcon />,
  order,
  smallBreakpointPx = 640,
  tipLabel,
}: AttributionControlProps) {
  const { t } = useTranslation();
  const { ngwMap } = useNgwMapContext();
  const olMap = ngwMap.mapAdapter?.map as OlMap | undefined;

  const [visible, setVisible] = useState(true);
  const [items, setItems] = useState<string[]>([]);
  const [isSmall, setIsSmall] = useState<boolean | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const expandCls = expandClassName ?? `${className}-expand`;
  const closeLabel = t("common.close");
  const title = tipLabel ?? t("attribution.title");

  useEffect(() => {
    if (!olMap) return;

    const container = olMap.getTargetElement();
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? container.clientWidth;
      setIsSmall(width <= smallBreakpointPx);
    });

    ro.observe(container);
    setIsSmall((container.clientWidth || 0) <= smallBreakpointPx);

    return () => {
      ro.disconnect();
    };
  }, [olMap, smallBreakpointPx]);

  const collectSourceAttributions = useCallback(
    (frameState: ViewStateLayerStateExtent) => {
      if (!olMap) return [];

      const visibleAttributions = new Set<unknown>(
        olMap
          .getAllLayers()
          .flatMap((layer) =>
            normalizeAttributions(layer.getAttributions(frameState as never)),
          ),
      );

      if (attributions !== undefined) {
        if (Array.isArray(attributions)) {
          attributions.forEach((item) => visibleAttributions.add(item));
        } else {
          visibleAttributions.add(attributions);
        }
      }

      return Array.from(visibleAttributions);
    },
    [attributions, olMap],
  );

  const update = useCallback(
    async (frameState: ViewStateLayerStateExtent | null) => {
      if (!frameState) {
        setVisible(false);
        return;
      }

      const nextAttributions = await Promise.all(
        collectSourceAttributions(frameState).map((attribution) =>
          toPromise(() => attribution),
        ),
      );
      const nextItems = nextAttributions.filter(
        (item): item is string => typeof item === "string" && item.length > 0,
      );

      setVisible(nextItems.length > 0);
      setItems((current) => (equals(nextItems, current) ? current : nextItems));
    },
    [collectSourceAttributions],
  );

  useEffect(() => {
    if (!olMap) return;

    const handler = (event: MapEvent) => {
      void update(event.frameState as ViewStateLayerStateExtent | null);
    };

    olMap.on("postrender", handler);
    return () => {
      olMap.un("postrender", handler);
    };
  }, [olMap, update]);

  const content = useMemo(() => {
    if (!visible) return null;

    return (
      <>
        {modalOpen && (
          <AttributionModal
            closeLabel={closeLabel}
            items={items}
            onClose={() => setModalOpen(false)}
            title={title}
          />
        )}
        <div
          className={classNames(className, "ol-unselectable", "ol-control", {
            "ol-uncollapsible": !isSmall,
          })}
        >
          {isSmall ? (
            <button
              className={expandCls}
              type="button"
              aria-label={title}
              title={title}
              onClick={() => setModalOpen(true)}
            >
              {label}
            </button>
          ) : (
            <AttributionList items={items} />
          )}
        </div>
      </>
    );
  }, [
    className,
    closeLabel,
    expandCls,
    isSmall,
    items,
    label,
    modalOpen,
    title,
    visible,
  ]);

  return (
    <MapControl className="map-ol-attribution-control" order={order}>
      {content}
    </MapControl>
  );
}
